/**
 * Audit page
 */

import { useState, useEffect } from 'react';
import { listAuditEvents } from '../services/audit';
import { useAuth } from '../contexts/AuthContext';
import type { AuditEvent } from '../../../shared/src/types';
import '../styles/Audit.css';

export default function Audit() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useAuth();

  const canView = user?.role === 'Admin' || user?.role === 'Supervisor';

  useEffect(() => {
    if (canView) {
      loadEvents();
    }
  }, [canView]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError('');
      const evts = await listAuditEvents({ limit: 100 });
      setEvents(evts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading audit events');
    } finally {
      setLoading(false);
    }
  };

  const getEventTypeLabel = (type: string) => {
    switch (type) {
      case 'create':
        return 'Crear';
      case 'update':
        return 'Actualizar';
      case 'delete':
        return 'Eliminar';
      case 'status_change':
        return 'Cambio de estado';
      case 'access':
        return 'Acceso';
      case 'auth_success':
        return 'Autenticación exitosa';
      case 'auth_failure':
        return 'Autenticación fallida';
      default:
        return type;
    }
  };

  if (!canView) {
    return (
      <div className="audit">
        <div className="error-message">
          No tienes permisos para ver los eventos de auditoría
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="audit">
        <div className="page-header">
          <h1>Auditoría</h1>
        </div>
        <div className="loading">Cargando eventos...</div>
      </div>
    );
  }

  return (
    <div className="audit">
      <div className="page-header">
        <h1>Auditoría</h1>
        <button className="btn-secondary" onClick={loadEvents}>
          Actualizar
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {events.length === 0 ? (
        <div className="empty-state">
          <p>No hay eventos de auditoría</p>
        </div>
      ) : (
        <div className="audit-table">
          <table>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Tipo</th>
                <th>Entidad</th>
                <th>ID Entidad</th>
                <th>Usuario</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id}>
                  <td>{new Date(event.created_at).toLocaleString('es-ES')}</td>
                  <td>
                    <span className={`event-type-badge event-type-${event.event_type}`}>
                      {getEventTypeLabel(event.event_type)}
                    </span>
                  </td>
                  <td>{event.entity_type}</td>
                  <td className="entity-id">{event.entity_id?.substring(0, 8) || '-'}</td>
                  <td>{event.user_id?.substring(0, 8) || 'Sistema'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

